import { Mail, Phone, MapPin, Send, Clock, MessageCircle, CheckCircle2 } from 'lucide-react';
import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = () => {
    if (!formData.name || !formData.email || !formData.message) {
      alert("Please fill all required fields.");
      return;
    }

    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
      setFormData({ name: '', email: '', message: '' });

      setTimeout(() => setIsSubmitted(false), 5000);
    }, 1500);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

 

  return (
    <>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-black to-slate-900 pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/5"></div>
        <div className="absolute top-20 right-10 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 left-10 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl"></div>

        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <div className="inline-block mb-6 px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full text-cyan-400 text-sm font-semibold">
            🤝 Let’s Talk About Your Investment & Risk
          </div>

          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Get in{' '}
            <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              Touch
            </span>
          </h1>

          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Share your goals, capital range, and time horizon. Our trading team
            will explain how our profit-sharing model works and what kind of
            risk you should realistically expect.
          </p>

          {/* Optional quick contact cards – keep commented if you don't want them visible */}
          {/*
          <div className="grid md:grid-cols-3 gap-4 max-w-4xl mx-auto mt-12">
            {contactMethods.map((method, i) => (
              <div
                key={i}
                className="bg-white/5 border border-white/10 rounded-xl p-6 hover:border-cyan-500/50 transition-all group cursor-pointer"
              >
                <div className={`w-12 h-12 mx-auto mb-4 bg-gradient-to-br ${method.color}  rounded-xl flex items-center justify-center group-hover:opacity-60 transition-all`}>
                  <method.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-sm text-gray-400 mb-1">{method.title}</h3>
                {method.link ? (
                  <a href={method.link} className="text-white font-semibold hover:text-cyan-400 transition-colors">
                    {method.value}
                  </a>
                ) : (
                  <p className="text-white font-semibold">{method.value}</p>
                )}
              </div>
            ))}
          </div>
          */}
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="bg-black py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Contact Form */}
            <div className="relative">
              <div className="rounded-2xl bg-gradient-to-br from-white/5 to-white/10 border border-white/20 p-8 backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                    <MessageCircle className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">
                      Tell Us About Your Goals
                    </h2>
                    <p className="text-gray-400 text-sm">
                      We&apos;ll get back to you with next steps & available
                      slots for a call.
                    </p>
                  </div>
                </div>

                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Doe"
                      className="w-full px-4 py-3 rounded-xl bg-black/50 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50 focus:ring-2 focus:ring-cyan-500/20 transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      className="w-full px-4 py-3 rounded-xl bg-black/50 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50 focus:ring-2 focus:ring-cyan-500/20 transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Your Message *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows="5"
                      placeholder="Example: Capital range, risk comfort (low / moderate / high), time horizon (e.g. 6–12 months), and any questions you have..."
                      className="w-full px-4 py-3 rounded-xl bg-black/50 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50 focus:ring-2 focus:ring-cyan-500/20 transition-all resize-none"
                    ></textarea>
                  </div>

                  <button
                    onClick={handleSubmit}
                    disabled={isSubmitting || isSubmitted}
                    className={`w-full px-6 py-4 rounded-xl font-bold transition-all transform hover:scale-105 flex items-center justify-center gap-2 ${
                      isSubmitted
                        ? 'bg-green-500 text-white'
                        : 'bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white shadow-lg shadow-cyan-500/30'
                    } ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        Sending...
                      </>
                    ) : isSubmitted ? (
                      <>
                        <CheckCircle2 className="w-5 h-5" />
                        Message Sent!
                      </>
                    ) : (
                      <>
                        Send Message
                        <Send className="w-5 h-5" />
                      </>
                    )}
                  </button>

                  <p className="text-xs text-gray-500 text-center">
                    By submitting this form, you acknowledge that trading in
                    financial markets involves risk of loss and that returns
                    are not guaranteed.
                  </p>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="space-y-6">
              {/* Office Location (optional – still commented, but updated text) */}
              {/*
              <div className="rounded-2xl bg-gradient-to-br from-white/5 to-white/10 border border-white/20 p-8 backdrop-blur-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-cyan-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">Our Office</h3>
                    <p className="text-gray-300 leading-relaxed">
                      Third Floor, Plot 95, Sector 82,<br />
                      JLPL Industrial Area, Mohali,<br />
                      Punjab 140306, India
                    </p>
                  </div>
                </div>
              </div>
              */}

              {/* Direct Contact (optional – still commented, but updated email) */}
              {/*
              <div className="rounded-2xl bg-gradient-to-br from-white/5 to-white/10 border border-white/20 p-8 backdrop-blur-sm">
                <h3 className="text-xl font-bold text-white mb-6">Direct Contact</h3>

                <div className="space-y-5">
                  <div className="flex items-center gap-4 p-4 bg-black/30 rounded-xl hover:bg-black/40 transition-all group cursor-pointer">
                    <div className="w-10 h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                      <Mail className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Email</p>
                      <a
                        href="mailto:support@kestren.com"
                        className="text-white font-medium hover:text-cyan-400 transition-colors"
                      >
                        support@kestren.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 p-4 bg-black/30 rounded-xl hover:bg-black/40 transition-all group cursor-pointer">
                    <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                      <Phone className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Phone / WhatsApp</p>
                      <a
                        href="tel:+918968881110"
                        className="text-white font-medium hover:text-cyan-400 transition-colors"
                      >
                        +91 89688 81110
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              */}

              {/* Business Hours */}
              <div className="rounded-2xl bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 p-8 backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-4">
                  <Clock className="w-6 h-6 text-cyan-400" />
                  <h3 className="text-xl font-bold text-white">Availability</h3>
                </div>
                <div className="space-y-2 text-gray-300">
                  <div className="flex justify-between">
                    <span>Monday - Friday:</span>
                    <span className="font-semibold text-white">9:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Saturday:</span>
                    <span className="font-semibold text-white">10:00 AM - 4:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Sunday:</span>
                    <span className="font-semibold text-white">Closed</span>
                  </div>
                  <div className="mt-4 pt-4 border-t border-cyan-500/30">
                    <p className="text-sm text-cyan-400">
                      For urgent matters, mention &quot;URGENT&quot; in your email
                      subject or message — we&apos;ll prioritise your query.
                    </p>
                  </div>
                </div>
              </div>

              {/* Disclaimer */}
              <div className="rounded-xl bg-white/5 border border-white/10 p-6">
                <p className="text-sm text-gray-400 leading-relaxed">
                  <strong className="text-gray-300">Risk Disclaimer:</strong>{' '}
                  We are professional traders, not financial advisers. Trading in
                  financial markets involves risk of loss, including loss of
                  capital. We do not offer guaranteed or fixed returns. Please
                  evaluate your own risk tolerance carefully before deciding to
                  invest with us.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        .bg-grid-white\\/5 {
          background-image: 
            linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
          background-size: 50px 50px;
        }
      `}</style>
    </>
  );
}
